// lib
import moment from 'moment'
import { List } from 'immutable'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import React, { useEffect, useState } from 'react'
import { preprocessText, escapeText } from '@aytm/lib/common/func'

// components
import LightBox from '@aytm/lib/rfd-ui/components/basic/light-box'

// styles
import '@aytm/lib/rfd-ui/styles/basic/video-popup.scss'

// constants
const FRAME_PADDING = 7
const LABEL_HEIGHT = 40

// every tutorial on Vimeo is 16:9; any other ratio letterboxes it inside the frame
const VIDEO_RATIO = 16 / 9
const MAX_VIDEO_WIDTH = 960
const MIN_VIDEO_WIDTH = 280
const VIEWPORT_WIDTH_SHARE = 0.9
const VIEWPORT_HEIGHT_SHARE = 0.85

// As wide as the cap allows while the whole frame, title bar included, still fits the window.
export const videoWidthFor = (viewportWidth, viewportHeight) =>
  Math.max(
    MIN_VIDEO_WIDTH,
    Math.floor(
      Math.min(
        MAX_VIDEO_WIDTH,
        viewportWidth * VIEWPORT_WIDTH_SHARE - 2 * FRAME_PADDING,
        (viewportHeight * VIEWPORT_HEIGHT_SHARE - LABEL_HEIGHT - 2 * FRAME_PADDING) * VIDEO_RATIO
      )
    )
  )

const currentVideoWidth = () => videoWidthFor(window.innerWidth, window.innerHeight)

// <Component>
VideoPopup.propTypes = {
  videos: PropTypes.instanceOf(List).isRequired,
  videoId: PropTypes.string,
  videoStartTime: PropTypes.number, // seconds
  show: PropTypes.bool,
  onClose: PropTypes.func
}

export default function VideoPopup(props) {
  const { show, videos, videoId, videoStartTime, onClose } = props

  const [currentVideoId, setCurrentVideoId] = useState('')
  const [startTime, setStartTime] = useState(0)
  const [videoWidth, setVideoWidth] = useState(currentVideoWidth)

  useEffect(() => {
    if (!show) return

    const onResize = () => setVideoWidth(currentVideoWidth())
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [show])

  const setVideo = () => {
    const { videos, videoId, videoStartTime } = props
    const currentVideoId = videoId || videos.getIn([0, 'id'])
    setCurrentVideoId(List.isList(currentVideoId) ? currentVideoId.get(0) : currentVideoId)
    setStartTime(videoStartTime || 0)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void setVideo(), [])

  useEffect(() => {
    if (videoId) setCurrentVideoId(videoId)
    setStartTime(videoStartTime || 0)
  }, [videoId, videoStartTime])

  const onCloseClick = event => {
    if ('function' === typeof onClose) {
      onClose()
    }
    try {
      event?.preventDefault?.()
      event?.stopPropagation?.()
      event?.stopImmediatePropagation?.()
    } catch {
      //
    }
  }

  const onPrevClick = event => {
    event.nativeEvent.stopImmediatePropagation()
    const currentVideoIndex = videos.findIndex(v =>
      List.isList(v.get('id')) ? v.get('id').some(id => id == currentVideoId) : v.get('id') == currentVideoId
    )
    const prevVideoIndex = Math.max(currentVideoIndex - 1, 0)
    const prevVideoId = videos.getIn([prevVideoIndex, 'id'])
    setCurrentVideoId(List.isList(prevVideoId) ? prevVideoId.get(0) : prevVideoId)
    setStartTime(0)
  }

  const onNextClick = event => {
    event.nativeEvent.stopImmediatePropagation()
    const currentVideoIndex = videos.findIndex(v =>
      List.isList(v.get('id')) ? v.get('id').some(id => id == currentVideoId) : v.get('id') == currentVideoId
    )
    const nextVideoIndex = Math.min(currentVideoIndex + 1, videos.size - 1)
    const nextVideoId = videos.getIn([nextVideoIndex, 'id'])
    setCurrentVideoId(List.isList(nextVideoId) ? nextVideoId.get(0) : nextVideoId)
    setStartTime(0)
  }

  const currentVideoIndex = videos.findIndex(v =>
    List.isList(v.get('id')) ? v.get('id').some(id => id == currentVideoId) : v.get('id') == currentVideoId
  )
  const currentVideo = videos.get(currentVideoIndex)
  const rel = currentVideo.get('rel')
  const autoplay = currentVideo.get('autoplay')

  // vimeo start time url param
  const duration = moment.duration(startTime, 'seconds')
  const vimeoStartTime = 't={mm}m{ss}s'.replace('{mm}', duration.minutes()).replace('{ss}', duration.seconds())

  const label = currentVideo.get('title')
  const params = []
  if (autoplay) params.push('autoplay=1')
  if (startTime) params.push(`start=${startTime}#${vimeoStartTime}`) // should be pushed as last param
  const src = `${currentVideo.get('url')}${currentVideo.get('code')}${
    (rel ? rel + '&amp;' : '?') + params.join('&amp;')
  }`
  const videoHeight = Math.round(videoWidth / VIDEO_RATIO)
  const iframe = `<iframe width="${videoWidth}" height="${videoHeight}" src="${src}" frameborder="0"  allow="${
    autoplay ? 'autoplay; ' : ''
  }fullscreen" allowfullscreen="true" allowscriptaccess="always"/>`

  return show ? (
    <LightBox onClose={onCloseClick}>
      <div
        className="rfd-ui-video-popup-frame"
        style={{
          width: `${videoWidth}px`,
          height: `${videoHeight + (label || 1 < videos.size ? LABEL_HEIGHT : 0)}px`
        }}
      >
        <div className="rfd-ui-video-popup-close-button" onMouseDown={onCloseClick}>
          <i className="icon-aytmfd-delete" />
        </div>
        <div className="rfd-ui-video-popup-video" dangerouslySetInnerHTML={{ __html: iframe }} />
        {(label || videos.size > 1) && (
          <div className="rfd-ui-video-popup-footer" style={{ height: `${LABEL_HEIGHT}px` }}>
            {label && (
              <div
                className="rfd-ui-video-popup-video-label"
                dangerouslySetInnerHTML={{ __html: preprocessText(escapeText(label)) }}
              />
            )}
            {videos.size > 1 && (
              <div className="rfd-ui-video-popup-video-controls">
                <i
                  className={classNames('icon-aytmfd-prev', {
                    disabled: 0 == currentVideoIndex
                  })}
                  onMouseDown={onPrevClick}
                />
                <i
                  className={classNames('icon-aytmfd-next', {
                    disabled: currentVideoIndex == videos.size - 1
                  })}
                  onMouseDown={onNextClick}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </LightBox>
  ) : null
}
